import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "./query-keys";

// Types
export interface Post {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  type: string;
  published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PostListItem {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  type: string;
  published_at: string | null;
  created_at: string;
}

export interface CreatePostInput {
  title: string;
  slug: string;
  excerpt?: string;
  content: string;
  type?: "essay" | "note";
  published?: boolean;
  published_at?: string;
}

export interface UpdatePostInput extends CreatePostInput {
  id: number;
}

// Fetch functions
async function fetchPosts(): Promise<PostListItem[]> {
  const response = await fetch("/api/posts");
  if (!response.ok) throw new Error("Failed to fetch posts");
  const data = (await response.json()) as { posts: PostListItem[] };
  return data.posts;
}

async function fetchPost(slug: string): Promise<Post> {
  const response = await fetch(`/api/posts/${slug}`);
  if (!response.ok) throw new Error("Failed to fetch post");
  const data = (await response.json()) as { post: Post };
  return data.post;
}

async function fetchAdminPosts(): Promise<Post[]> {
  const response = await fetch("/api/admin/posts");
  if (!response.ok) throw new Error("Failed to fetch posts");
  const data = (await response.json()) as { posts: Post[] };
  return data.posts;
}

async function fetchAdminPost(id: number): Promise<Post> {
  const response = await fetch(`/api/admin/posts/${id}`);
  if (!response.ok) throw new Error("Failed to fetch post");
  const data = (await response.json()) as { post: Post };
  return data.post;
}

async function createPost(input: CreatePostInput): Promise<Post> {
  const response = await fetch("/api/admin/posts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error("Failed to create post");
  const data = (await response.json()) as { post: Post };
  return data.post;
}

async function updatePost(input: UpdatePostInput): Promise<Post> {
  const { id, ...body } = input;
  const response = await fetch(`/api/admin/posts/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error("Failed to update post");
  const data = (await response.json()) as { post: Post };
  return data.post;
}

async function deletePost(id: number): Promise<void> {
  const response = await fetch(`/api/admin/posts/${id}`, {
    method: "DELETE",
  });
  if (!response.ok) throw new Error("Failed to delete post");
}

// Hooks
export function usePosts() {
  return useQuery({
    queryKey: queryKeys.posts.list(),
    queryFn: fetchPosts,
  });
}

export function usePost(slug: string) {
  return useQuery({
    queryKey: queryKeys.posts.detail(slug),
    queryFn: () => fetchPost(slug),
    enabled: !!slug,
  });
}

export function useAdminPosts() {
  return useQuery({
    queryKey: queryKeys.posts.admin.list(),
    queryFn: fetchAdminPosts,
  });
}

export function useAdminPost(id: number) {
  return useQuery({
    queryKey: queryKeys.posts.admin.detail(id),
    queryFn: () => fetchAdminPost(id),
    enabled: !!id,
  });
}

export function useCreatePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPost,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.admin.all });
    },
  });
}

export function useUpdatePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePost,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.admin.all });
      queryClient.setQueryData(queryKeys.posts.admin.detail(data.id), data);
    },
  });
}

export function useDeletePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePost,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.admin.all });
    },
  });
}
